import { Clerk } from '@clerk/clerk-js'
import { LoaderFunction, LoaderFunctionArgs, redirect, useLoaderData, useRouteLoaderData } from 'react-router-dom'
import { parseKycStatus } from '@schemas'

// Capacitor
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'

// One Signal
import { OneSignal } from '@capacitor/one-signal'


const clerkIsLoaded = () => (!!window.Clerk && !!window.Clerk.loaded)
export const waitForClerkUser = async (Clerk: Clerk, timeoutMs = 1500, intervalMs = 100) => {
    if (Clerk.user) return Clerk.user

    return await new Promise<Clerk['user']>((resolve) => {
        const start = Date.now()
        const interval = setInterval(() => {
            if (Clerk.user) {
                clearInterval(interval)
                resolve(Clerk.user)
                return
            }

            if (Date.now() - start >= timeoutMs) {
                clearInterval(interval)
                resolve(null)
            }
        }, intervalMs)
    })
}
export const refreshClerkUser = async (Clerk: Clerk) => {
    if (!Clerk.user) return null

    try {
        await Clerk.user.reload()
    } catch (error) {
        console.error('Failed to refresh Clerk user metadata', error)
    }

    return Clerk.user
}
export const fetchClerk = () => new Promise<Clerk>((resolve, reject) => {
    if (clerkIsLoaded()) {
        resolve(window.Clerk!)
        return
    }

    let count = 0
    const interval = setInterval(() => {
        console.log("Clerk Check Count:", count)
        const isLoaded = clerkIsLoaded()
        if (count >= 10 || isLoaded) {
            clearInterval(interval)
            if (isLoaded) resolve(window.Clerk!)
            else reject("Failed to load Clerk")
            return
        }

        count++
    }, 1000)
})

type AuthenticatedLoaderFunction<Context = unknown, ReturnData = ReturnType<LoaderFunction>> = (
    (args: LoaderFunctionArgs<Context>, Clerk: Clerk, handlerCtx?: unknown) => ReturnData
) & { hydrate?: boolean }
type KycVerifiedLoaderFunction<Context = unknown, ReturnData = ReturnType<LoaderFunction>> = (
    (args: LoaderFunctionArgs<Context>, isKycVerified: boolean, isNewUser: boolean, Clerk: Clerk, handlerCtx?: unknown) => ReturnData
) & { hydrate?: boolean }

export const createLoader = <Loader = LoaderFunction>(loader: Loader) => loader
export const createAuthenticatedLoader = <AuthenticatedLoader extends AuthenticatedLoaderFunction>(loader: AuthenticatedLoader) => {
    return createLoader(async (args, handlerCtx) => {
        const params = new URLSearchParams()

        if (Capacitor.isNativePlatform()) {
            let url: string | undefined = undefined

            const launchUrl = await App.getLaunchUrl()
            if (launchUrl && launchUrl.url !== '') url = launchUrl.url

            if (!url) {
                const launchUrl = await OneSignal.getLaunchUrl()
                if (launchUrl && launchUrl.url !== '') url = launchUrl.url
            }

            if (url) {
                const sanitizedPath = url
                    .replace(/^https?:\/\/(?:dev\.)?group\.splitcard\.com/i, '')
                    .replace(/^:\/\//, '/')
                    .replace(/^\/+/, '/')
                    .trim() || ''

                const targetPath = sanitizedPath.length > 0 ? sanitizedPath : '/'
                params.set('from', targetPath)
            }
        }

        const Clerk = await fetchClerk()
        if (!Clerk.user && Capacitor.isNativePlatform()) {
            await waitForClerkUser(Clerk)
        }

        if (!Clerk.user) {
            if (!params.has('from'))
                params.set("from", new URL(document.location.href).pathname)

            return redirect(`/sign-in?${params.toString()}`)
        }
        else return await loader(args, Clerk, handlerCtx) as ReturnType<AuthenticatedLoader>
    })
}
export const createKycVerifiedLoader = <KycVerifiedLoader extends KycVerifiedLoaderFunction>(loader: KycVerifiedLoader) => {
    return createAuthenticatedLoader(async (args, Clerk, handlerCtx) => {
        const user = Clerk.user
        const kycStatus = parseKycStatus(user?.publicMetadata.kyc)
        const isKycVerified = kycStatus.status === 'approved' || kycStatus.status === 'completed'
        const isNewUser = user?.publicMetadata.newUser || true
        return loader(args, isKycVerified, isNewUser, Clerk, handlerCtx) as ReturnType<KycVerifiedLoader>
    })
}


type LoaderData<TLoaderFn extends LoaderFunction> = Awaited<ReturnType<TLoaderFn>> extends Response | infer D ? D : never
export const useLoader = <Loader extends LoaderFunction>() => useLoaderData() as LoaderData<Loader>
export const useLoaderId = <Loader extends LoaderFunction>(routeId: string) => useRouteLoaderData(routeId) as LoaderData<Loader>
